import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import LogoutModal from '../../components/LogoutModal';
import Footer from '../../components/Footer';

interface UserDetails {
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  created_at?: string;
  last_login?: string;
}

const UserAccount: React.FC = () => {
  const { user, logout } = useAuth();
  const [userDetails, setUserDetails] = useState<UserDetails>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: 'India'
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('account');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/profile');
      const profileData = response.data.data;
      
      setUserDetails({
        name: profileData.name || '',
        email: profileData.email || '',
        phone: profileData.phone || '',
        address: profileData.address || '',
        city: profileData.city || '',
        state: profileData.state || '',
        country: profileData.country || 'India',
        created_at: profileData.created_at,
        last_login: profileData.last_login
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserDetails(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    // Phone validation
    if (userDetails.phone && !/^[0-9+\-\s()]{10,15}$/.test(userDetails.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    // City validation
    if (userDetails.city && userDetails.city.length < 2) {
      errors.city = 'City name must be at least 2 characters';
    }

    // State validation
    if (userDetails.state && userDetails.state.length < 2) {
      errors.state = 'State name must be at least 2 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateProfile = async () => {
    // Validate form before submitting
    if (!validateForm()) {
      return;
    }

    try {
      setUpdateError('');
      await api.put('/auth/profile', {
        phone: userDetails.phone,
        address: userDetails.address,
        city: userDetails.city,
        state: userDetails.state,
        country: userDetails.country
      });
      
      setIsEditing(false);
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 5000);

      // Refresh profile data
      await fetchUserProfile();
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setUpdateError(error.response?.data?.message || 'Failed to update profile. Please try again.');
      setTimeout(() => setUpdateError(''), 5000);
    }
  };

  const handleNavigation = (tabId: string, path: string) => {
    setActiveTab(tabId);
    window.location.href = path;
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const sidebarItems = [
    { id: 'home', label: 'Home', icon: '🏠', path: '/user' },
    { id: 'account', label: 'My Account', icon: '👤', path: '/user/account' },
    { id: 'activity', label: 'My Activity', icon: '📊', path: '/user/activity' }
  ];

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', fontFamily: 'Arial, sans-serif' }}>
      {/* Sidebar */}
      <div style={{ 
        position: 'fixed', 
        left: 0, 
        top: 0, 
        width: '250px', 
        height: '100vh', 
        backgroundColor: '#fff', 
        borderRight: '1px solid #ddd',
        padding: '20px',
        boxSizing: 'border-box'
      }}>
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ margin: 0, color: '#333' }}>YuvanApi</h2>
        </div>
        
        <nav>
          {sidebarItems.map(item => (
            <div key={item.id} style={{ marginBottom: '10px' }}>
              <button
                style={{
                  width: '100%',
                  padding: '12px',
                  border: 'none',
                  backgroundColor: activeTab === item.id ? '#007bff' : 'transparent',
                  color: activeTab === item.id ? 'white' : '#333',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '14px'
                }}
                onClick={() => {
                  handleNavigation(item.id, item.path);
                  setIsMobileMenuOpen(false);
                }}
              >
                {item.icon} {item.label}
              </button>
            </div>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div style={{ marginLeft: '250px', padding: '20px' }}>
        {/* Header */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ 
              margin: 0, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: '28px',
              fontWeight: '700',
              letterSpacing: '-0.5px'
            }}>My Account</h1>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
                onClick={() => window.location.href = '/user'}
              >
                🏠 Dashboard
              </button>
              <button 
                style={{
                  padding: '8px 16px',
                  border: '1px solid #dc3545',
                  borderRadius: '4px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  cursor: 'pointer'
                }}
                onClick={handleLogoutClick}
              >
                🚪 Logout
              </button>
            </div>
          </div>
        </div>

        {/* Account Content */}
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px' }}>Loading...</div>
          ) : (
            <>
              {updateSuccess && (
                <div style={{ 
                  backgroundColor: '#d4edda', 
                  border: '1px solid #c3e6cb', 
                  color: '#155724', 
                  padding: '15px', 
                  borderRadius: '4px', 
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '20px' }}>✅</span>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>Success!</div>
                    <div style={{ fontSize: '14px' }}>Profile updated successfully!</div>
                  </div>
                </div>
              )}

              {updateError && (
                <div style={{ 
                  backgroundColor: '#f8d7da', 
                  border: '1px solid #f5c6cb', 
                  color: '#721c24', 
                  padding: '15px', 
                  borderRadius: '4px', 
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '20px' }}>❌</span>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>Error!</div>
                    <div style={{ fontSize: '14px' }}>{updateError}</div>
                  </div>
                </div>
              )}

              {/* Profile Section */}
              <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2 style={{ margin: 0, color: '#333' }}>Profile Information</h2>
                  <button
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: isEditing ? '#dc3545' : '#007bff',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    {isEditing ? '❌ Cancel' : '✏️ Edit'}
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                  {/* Full Name */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={userDetails.name}
                      disabled={true}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: '#6c757d', margin: '5px 0 0 0' }}>Name cannot be changed</p>
                  </div>

                  {/* Email Address */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={userDetails.email}
                      disabled={true}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f8f9fa',
                        color: '#6c757d'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: '#6c757d', margin: '5px 0 0 0' }}>Email cannot be changed</p>
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={userDetails.phone}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `1px solid ${validationErrors.phone ? '#dc3545' : '#ddd'}`,
                        borderRadius: '4px',
                        backgroundColor: isEditing ? 'white' : '#f8f9fa'
                      }}
                      placeholder="Enter phone number"
                    />
                    {validationErrors.phone && (
                      <p style={{ fontSize: '12px', color: '#dc3545', margin: '5px 0 0 0' }}>
                        ⚠️ {validationErrors.phone}
                      </p>
                    )}
                  </div>

                  {/* City */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>City</label>
                    <input
                      type="text"
                      name="city"
                      value={userDetails.city}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `1px solid ${validationErrors.city ? '#dc3545' : '#ddd'}`,
                        borderRadius: '4px',
                        backgroundColor: isEditing ? 'white' : '#f8f9fa'
                      }}
                      placeholder="Enter city"
                    />
                    {validationErrors.city && (
                      <p style={{ fontSize: '12px', color: '#dc3545', margin: '5px 0 0 0' }}>
                        ⚠️ {validationErrors.city}
                      </p>
                    )}
                  </div>

                  {/* State */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>State</label>
                    <input
                      type="text"
                      name="state"
                      value={userDetails.state}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: `1px solid ${validationErrors.state ? '#dc3545' : '#ddd'}`,
                        borderRadius: '4px',
                        backgroundColor: isEditing ? 'white' : '#f8f9fa'
                      }}
                      placeholder="Enter state"
                    />
                    {validationErrors.state && (
                      <p style={{ fontSize: '12px', color: '#dc3545', margin: '5px 0 0 0' }}>
                        ⚠️ {validationErrors.state}
                      </p>
                    )}
                  </div>

                  {/* Country */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>Country</label>
                    <input
                      type="text"
                      name="country"
                      value={userDetails.country}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: isEditing ? 'white' : '#f8f9fa'
                      }}
                      placeholder="Enter country"
                    />
                  </div>

                  {/* Address */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>Address</label>
                    <input
                      type="text"
                      name="address"
                      value={userDetails.address}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: isEditing ? 'white' : '#f8f9fa'
                      }}
                      placeholder="Enter address"
                    />
                  </div>
                </div>

                {isEditing && (
                  <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee', textAlign: 'right' }}>
                    <button 
                      style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '4px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                      onClick={handleUpdateProfile}
                    >
                      💾 Save Changes
                    </button>
                  </div>
                )}
              </div>

              {/* Account Info Section */}
              <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{ margin: '0 0 20px 0', color: '#333' }}>Account Information</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  {[
                    { icon: '📅', label: 'Account Created', value: formatDate(userDetails.created_at) },
                    { icon: '🕐', label: 'Last Login', value: formatDate(userDetails.last_login) },
                    { icon: '👤', label: 'Account Type', value: user?.role || 'User' },
                    { icon: '✅', label: 'Account Status', value: 'Active' }
                  ].map((info, index) => (
                    <div key={index} style={{ 
                      padding: '15px', 
                      border: '1px solid #eee', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '15px'
                    }}>
                      <div style={{ fontSize: '24px' }}>{info.icon}</div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                          {info.label}
                        </div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>
                          {info.value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <Footer />
      </div>

      {/* Modals */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
};

export default UserAccount;
